import { useState } from 'react';
import type { Classification, EventRecord } from '../../shared/contracts';
import { deriveSubjectExclusionPattern, extractSenderEmail } from '../../domain/rules';
import { api } from '../api';

export function IgnoreSourceDialog({ event, busy, onClose, onConfirm }: {
  event: EventRecord;
  busy: boolean;
  onClose: () => void;
  onConfirm: (pattern: string, field: 'subject' | 'email') => Promise<void>;
}) {
  const [field, setField] = useState<'subject' | 'email'>('subject');
  const subjectPattern = deriveSubjectExclusionPattern(event.subject);
  const emailPattern = extractSenderEmail(event.sender);
  const pattern = field === 'email' ? emailPattern : subjectPattern;
  const validPattern = pattern.trim().length >= 4 && pattern.trim().length <= 200;
  const confirm = (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !validPattern) return;
    void onConfirm(pattern.trim(), field);
  };
  return <div className="modal-backdrop" role="presentation" data-swipe-ignore onMouseDown={(e) => !busy && e.target === e.currentTarget && onClose()}><div className="modal ignore-dialog" role="dialog" aria-modal="true" aria-labelledby="ignore-title" aria-busy={busy}>
    <div className="modal-header"><h2 id="ignore-title">今後拾わない設定</h2><button onClick={onClose} aria-label="閉じる" disabled={busy}>×</button></div>
    <div className="ignore-source-summary"><span>今回のメール</span><strong>{event.subject}</strong></div>
    <form onSubmit={confirm}>
      <fieldset className="ignore-options" disabled={busy}><legend>今後拾わない条件を選ぶ</legend>
        <label className={field === 'subject' ? 'active' : ''}><input type="radio" name="ignore-field" checked={field === 'subject'} onChange={() => setField('subject')} /><span><strong>件名にこの文字を含むメール</strong><b className="ignore-pattern">「{subjectPattern}」</b><small>送信者に関係なく、件名にこの文字を含むメールが対象です。</small></span></label>
        <label className={field === 'email' ? 'active' : ''}><input type="radio" name="ignore-field" checked={field === 'email'} onChange={() => setField('email')} /><span><strong>送信元アドレスにこの文字を含むメール</strong><b className="ignore-pattern">「{emailPattern}」</b><small>件名に関係なく、アドレスにこの文字を含むメールが対象です。</small></span></label>
      </fieldset>
      {!validPattern && <p role="alert">条件の文字は4〜200文字が必要です。別の条件を選ぶか、設定から条件を追加してください。</p>}
      <p>このメールから作った予定も削除します。元のメールはGmailに残ります。</p>
      <div className="ignore-note"><span>条件は「設定」で編集・解除できます。</span><span>「必ず拾う」「優先する」にも一致するメールは、そちらの条件が優先されます。</span></div>
      <div className="modal-actions"><button type="button" className="button secondary" onClick={onClose} disabled={busy}>戻る</button><button className="button danger" disabled={busy || !validPattern}>{busy ? '設定中…' : 'この条件で拾わない'}</button></div>
    </form>
  </div></div>;
}

export function EditDialog({ event, onClose, onSaved }: {
  event: EventRecord;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [title, setTitle] = useState(event.title);
  const [classification, setClassification] = useState<Classification>(event.classification === '候補' || event.classification === '要確認' ? '要確認' : '予定');
  const [start, setStart] = useState(event.allDay ? event.startDateTime : event.startDateTime.slice(0, 16));
  const [end, setEnd] = useState(event.allDay ? event.endDateTime : event.endDateTime.slice(0, 16));
  const [allDay, setAllDay] = useState(event.allDay);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const save = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setError('');
    const withZone = (value: string) => allDay ? value : `${value}:00+09:00`;
    try {
      await api(`/api/events/${event.id}`, { method: 'PATCH', body: JSON.stringify({ title, classification, startDateTime: withZone(start), endDateTime: end ? withZone(end) : null, allDay }) });
      await onSaved();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '予定を変更できませんでした');
    } finally {
      setSaving(false);
    }
  };
  return <div className="modal-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}><div className="modal" role="dialog" aria-modal="true" aria-labelledby="edit-title">
    <div className="modal-header"><h2 id="edit-title">予定を編集</h2><button onClick={onClose} aria-label="閉じる" disabled={saving}>×</button></div>
    <form onSubmit={save} className="form-stack">
      {error && <div className="alert error">{error}</div>}
      <label>予定名<input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={160} /></label>
      <label>分類<select value={classification} onChange={(e) => setClassification(e.target.value as Classification)}>{['予定', '要確認'].map((value) => <option key={value}>{value}</option>)}</select></label>
      <label className="check-line"><input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />終日予定</label>
      <label>開始<input type={allDay ? 'date' : 'datetime-local'} value={start} onChange={(e) => setStart(e.target.value)} required /></label>
      <label>終了（任意）<input type={allDay ? 'date' : 'datetime-local'} value={end} onChange={(e) => setEnd(e.target.value)} /></label>
      <button className="button primary big" disabled={saving}>{saving ? '保存中…' : '変更を保存'}</button>
    </form>
  </div></div>;
}
